import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled, {css} from 'styled-components'

import CompanyInput from '@/components/company-input'
import Textarea from '@/components/textarea'
import type {ExpenseReportGeneratorQuery} from '@/graphql-types'
import useCurrency from '@/hooks/use-currency'
import {useWizardContext} from '@/hooks/use-step-wizard'
import Button from '@helios/shared/components/button'
import Input from '@helios/shared/components/input'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {useLocale} from '@helios/shared/providers/locale'
import {text, Text} from '@helios/shared/styles/typography'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

import {Accordion, AccordionItem} from '../accordion'
import {BREAKPOINT_SM, BREAKPOINT_LG} from '../constants'
import type {Dispatch} from '../expense-report-generator'
import {ActionType} from '../expense-report-generator'
import {useCurrencyFormat} from '../hooks'
import type {Expense, ExpenseReportGeneratorVariant} from '../types'

interface Props {
    dispatch: Dispatch
    expenses: Expense[]
    onShowPreview?: () => void
    content: PrismicData<ExpenseReportGeneratorQuery, 'allPrismicExpenseReportGenerator'>['data']
    variant: ExpenseReportGeneratorVariant
}

const ExpensesDashboard = ({expenses, onShowPreview, dispatch, content, variant}: Props) => {
    const currency = useCurrency()
    const currencyFormat = useCurrencyFormat()
    const isSmallScreen = useMediaQuery.max(BREAKPOINT_SM)
    const isLargeScreen = useMediaQuery.min(BREAKPOINT_LG)

    const [activeRowIndex, setActiveRowIndex] = React.useState(0)
    const {navigate} = useWizardContext()

    const amountTotal = expenses.reduce((sum, e) => sum + (e.amount ?? 0), 0)
    const formattedTotal = currencyFormat.format(amountTotal)

    const Footer = () => (
        <StyledFooter>
            <FooterTotal>
                <Text variant="title-base">{content.total}</Text>
                <Text variant="title-base">{formattedTotal}</Text>
            </FooterTotal>

            <FooterButtons>
                {!isSmallScreen && (
                    <Button onClick={() => onShowPreview?.()} variant="outline">
                        {content.preview}
                    </Button>
                )}

                <FooterRight>
                    <Button
                        onClick={() => {
                            dispatch({type: ActionType.EXPENSE_ADDED})
                            track(TrackingEvent.ERGActioned, {
                                action: 'expense_added',
                                variant,
                            })
                        }}
                        variant="outline"
                    >
                        {content.addExpense}
                    </Button>

                    <Button onClick={navigate.next}>{content.generateExpenseReport}</Button>
                </FooterRight>
            </FooterButtons>
        </StyledFooter>
    )

    // Bigger screens (e.g. desktop)
    if (isLargeScreen) {
        return (
            <Wrapper>
                <Table>
                    <VisuallyHidden as="caption">{content.expensesDashboard}</VisuallyHidden>

                    <THead>
                        <TR>
                            <TH scope="column">{content.date}</TH>
                            <TH scope="column">{content.description}</TH>
                            <TH scope="column">{content.merchant}</TH>
                            <TH scope="column">{content.category}</TH>
                            <TH scope="column">{content.project}</TH>
                            <TH scope="column">
                                {content.amount} ({currency.symbol})
                            </TH>
                        </TR>
                    </THead>

                    {expenses.map((expense, i) => (
                        <ExpenseRow
                            key={expense.id}
                            {...expense}
                            onDeleteExpense={() => {
                                dispatch({
                                    type: ActionType.EXPENSE_DELETED,
                                    id: expense.id,
                                })
                                track(TrackingEvent.ERGActioned, {
                                    action: 'expense_deleted',
                                    variant,
                                })
                            }}
                            isActive={activeRowIndex === i}
                            setActiveRowIndex={() => setActiveRowIndex(i)}
                            dispatch={dispatch}
                            content={content}
                        />
                    ))}
                </Table>

                <Footer />
            </Wrapper>
        )
    }

    // Smaller screens (e.g. mobile / tablet)
    return (
        <StyledAccordion>
            {expenses.map((expense, i) => (
                <ExpenseAccordionItem
                    key={expense.id}
                    {...expense}
                    onDeleteExpense={() => {
                        dispatch({
                            type: ActionType.EXPENSE_DELETED,
                            id: expense.id,
                        })
                        track(TrackingEvent.ERGActioned, {
                            action: 'expense_deleted',
                            variant,
                        })
                    }}
                    expenseNumber={i + 1}
                    dispatch={dispatch}
                    content={content}
                />
            ))}

            <Footer />
        </StyledAccordion>
    )
}

type ExpenseRowProps = Expense &
    Pick<Props, 'dispatch'> & {
        isActive?: boolean
        setActiveRowIndex: () => void
        onDeleteExpense?: () => void
        content: PrismicData<
            ExpenseReportGeneratorQuery,
            'allPrismicExpenseReportGenerator'
        >['data']
    }

const ExpenseRow = ({
    id,
    date,
    description,
    merchant,
    category,
    project,
    amount,
    onDeleteExpense,
    isActive,
    setActiveRowIndex,
    dispatch,
    content,
}: ExpenseRowProps) => {
    const currentLocale = useLocale()

    return (
        <TBody>
            <TR>
                <TD>
                    <Input
                        onFocus={setActiveRowIndex}
                        value={date}
                        type="date"
                        placeholder={new Intl.DateTimeFormat(currentLocale.code).format(new Date())}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_DATE_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                </TD>
                <TD>
                    <Textarea
                        onFocus={setActiveRowIndex}
                        value={description}
                        placeholder={content.description}
                        rows={1}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_DESCRIPTION_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                </TD>
                <TD>
                    <CompanyInput
                        onFocus={setActiveRowIndex}
                        value={merchant}
                        placeholder={content.merchant}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_MERCHANT_UPDATED,
                                id,
                                value,
                            })
                        }}
                        onSelect={(company) => {
                            dispatch({
                                type: ActionType.EXPENSE_MERCHANT_UPDATED,
                                id,
                                value: company.name,
                            })
                        }}
                    />
                </TD>
                <TD>
                    <Input
                        onFocus={setActiveRowIndex}
                        value={category}
                        placeholder={content.category}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_CATEGORY_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                </TD>
                <TD>
                    <Input
                        onFocus={setActiveRowIndex}
                        value={project}
                        placeholder={content.project}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_PROJECT_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                </TD>
                <TD>
                    <AmountInput
                        onFocus={setActiveRowIndex}
                        type="number"
                        value={amount}
                        placeholder={content.amount}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_AMOUNT_UPDATED,
                                id,
                                value: value === '' ? undefined : Number(value),
                            })
                        }}
                    />
                </TD>
            </TR>

            {isActive && (
                <DeleteExpenseButtonRow>
                    <TD colSpan={6}>
                        <DeleteExpenseButton onClick={onDeleteExpense}>
                            {content.deleteExpense}
                        </DeleteExpenseButton>
                    </TD>
                </DeleteExpenseButtonRow>
            )}
        </TBody>
    )
}

type ExpenseAccordionItemProps = Expense &
    Pick<Props, 'dispatch'> & {
        expenseNumber: number
        onDeleteExpense?: () => void
        content: PrismicData<
            ExpenseReportGeneratorQuery,
            'allPrismicExpenseReportGenerator'
        >['data']
    }

const ExpenseAccordionItem = ({
    id,
    expenseNumber,
    date,
    description,
    merchant,
    category,
    project,
    amount,
    onDeleteExpense,
    dispatch,
    content,
}: ExpenseAccordionItemProps) => {
    const currency = useCurrency()
    const currentLocale = useLocale()

    return (
        <StyledAccordionItem
            title={description || `${content.expense} ${expenseNumber}`}
            initialOpen={expenseNumber === 1}
        >
            <AccordionContentWrapper>
                <AccordionGrid>
                    <Input
                        value={date}
                        label={content.date}
                        type="date"
                        placeholder={new Intl.DateTimeFormat(currentLocale.code).format(new Date())}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_DATE_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                    <Textarea
                        value={description}
                        label={content.description}
                        placeholder={content.description}
                        rows={1}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_DESCRIPTION_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                    <CompanyInput
                        value={merchant}
                        label={content.merchant}
                        placeholder={content.merchant}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_MERCHANT_UPDATED,
                                id,
                                value,
                            })
                        }}
                        onSelect={(company) => {
                            dispatch({
                                type: ActionType.EXPENSE_MERCHANT_UPDATED,
                                id,
                                value: company.name,
                            })
                        }}
                    />
                    <Input
                        value={category}
                        label={content.category}
                        placeholder={content.category}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_CATEGORY_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />
                    <Input
                        value={project}
                        label={content.project}
                        placeholder={content.project}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_PROJECT_UPDATED,
                                id,
                                value,
                            })
                        }}
                    />

                    <Input
                        type="number"
                        value={amount}
                        label={`${content.amount} (${currency.symbol})`}
                        placeholder={content.amount}
                        onChange={(value) => {
                            dispatch({
                                type: ActionType.EXPENSE_AMOUNT_UPDATED,
                                id,
                                value: value === '' ? undefined : Number(value),
                            })
                        }}
                    />
                </AccordionGrid>

                <DeleteExpenseButton onClick={onDeleteExpense}>
                    {content.deleteExpense}
                </DeleteExpenseButton>
            </AccordionContentWrapper>
        </StyledAccordionItem>
    )
}

const cellPadding = css`
    padding: 0;

    :not(:first-child) {
        padding-left: ${tokens.spacing12};
    }

    :not(:last-child) {
        padding-right: ${tokens.spacing12};
    }
`

const Wrapper = styled.section`
    display: grid;
    row-gap: ${tokens.spacing48};
    padding: ${tokens.spacing24};
    background: ${colors.background.default};
    border-radius: ${radii.arc8};
`

const AmountInput = styled(Input)`
    text-align: right;

    &::placeholder {
        text-align: left;
    }
`

const StyledFooter = styled.footer`
    display: grid;
    row-gap: ${tokens.spacing40};
    justify-items: end;

    ${mq.between(BREAKPOINT_SM, BREAKPOINT_LG)} {
        padding: ${tokens.spacing40};
        background: ${colors.background.default};
        border-radius: ${radii.arc8};
    }
`

const FooterRight = styled.div`
    display: grid;
    gap: inherit;
    grid-auto-flow: row;
    width: 100%;

    ${mq.min(BREAKPOINT_SM)} {
        grid-auto-flow: column;
    }
`

const FooterTotal = styled.div`
    display: grid;
    width: 100%;
    grid-auto-flow: column;
    column-gap: ${tokens.spacing40};
    justify-content: space-between;

    ${mq.min(BREAKPOINT_SM)} {
        justify-content: end;
    }
`

const FooterButtons = styled.div`
    display: grid;
    column-gap: ${tokens.spacing48};
    row-gap: ${tokens.spacing20};
    grid-auto-flow: column;
    width: 100%;

    ${mq.min(BREAKPOINT_SM)} {
        justify-content: space-between;
    }
`

const Table = styled.table`
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: collapse;
`

const THead = styled.thead`
    ${text({variant: 'paragraph-base', color: 'muted'})}
`

const TBody = styled.tbody({})

const TFoot = styled.tfoot`
    ${text({variant: 'title-base'})}
`

const TR = styled.tr({})

const TH = styled.th`
    ${cellPadding}

    text-align: left;
    vertical-align: top;

    ${THead} & {
        padding-bottom: ${tokens.spacing12};
        font-weight: inherit;
        border-bottom: 1px solid ${tokens.shade500};
    }
`

const DeleteExpenseButtonRow = styled(TR)`
    text-align: right;
`

const DeleteExpenseButton = styled.button`
    ${text({variant: 'label-base', color: 'lightMuted'})}

    width: auto;
    padding: unset;
    text-decoration: underline;
    cursor: pointer;
    background: unset;
    border: unset;
`

const TD = styled.td`
    ${cellPadding}

    vertical-align: top;

    ${TBody} & {
        padding-top: ${tokens.spacing16};
    }

    ${TFoot} & {
        padding-top: ${tokens.spacing60};
        text-align: right;
    }

    ${DeleteExpenseButtonRow} & {
        padding-top: unset;
        padding-bottom: ${tokens.spacing24};
    }
`

const StyledAccordion = styled(Accordion)`
    row-gap: ${tokens.spacing20};

    ${mq.min(BREAKPOINT_SM)} {
        row-gap: ${tokens.spacing32};
    }
`

const StyledAccordionItem = styled(AccordionItem)`
    background: ${tokens.shade000};

    ${mq.max(BREAKPOINT_SM)} {
        border: 1px solid ${tokens.shade900};
    }
`

const AccordionContentWrapper = styled.div`
    display: grid;
    justify-items: end;
    row-gap: ${tokens.spacing20};
`

const AccordionGrid = styled.div`
    display: grid;
    width: 100%;
    column-gap: ${tokens.spacing32};
    row-gap: ${tokens.spacing20};

    ${mq.min(BREAKPOINT_SM)} {
        grid-template-columns: repeat(3, 1fr);
    }
`

export default ExpensesDashboard
